import React, { useEffect, useState } from "react";
import { RouteComponentProps, useLocation } from "react-router";
import { Box, makeStyles, Container, Typography, withStyles } from "@material-ui/core";
import { colors, Loader } from "swyft-react-core";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../app/rootReducer";
import {
  fetchQuote,
  closeDeductionInfoModal,
  addAddonQuote,
  removeAddonQuote,
  changeQuantityAddonQuote,
  setInfoAddon,
  addSubscriptionQuote,
  removeSubscriptionQuote,
  syncQuote
} from "../../store/QuoteSlice";
import { PageState } from "../../api/Types";
import { QuoteLineItem } from "../../api/Quote/QuoteLineItem";
import { DeductionInfoModal } from "./DeductionInfoModal";
import { QuoteTop } from "./QuoteTop";
import { QuoteGuarantee } from "./QuoteGuarantee";
import { allowCustomerEdits, hasAddons, hasSubscriptions, quoteAvailableSubscriptions } from "../../app/utils";
import { ReviewContainer } from "./ReviewContainer";
import { useCookies } from "react-cookie";
import { CookieQuoteId, FeatureSchedule } from "../../app/consts";
import { QuoteRecommends } from "./QuoteRecommends";
import { AddonForm } from "../../api/Addon/AddonApi";
import { toast } from "react-toastify";
import QuoteAddonInfoDialog from "./components/QuoteAddonInfoDialog";
import QuoteRemoveDialog from "./components/QuoteRemoveDialog";
import { ProductSubscription } from "../../api/Product/ProductSubscriptionApi";
import { fetchProductSubscriptions } from "../../store/ProductSubscriptionSlice";
import { mapProductSubscriptionToForm, QuoteSubscriptionForm } from "../../api/QuoteSubscription/QuoteSubscriptionApi";
import { EEventType, eventAnalytic } from "../../app/analytics";
import { EventSlotQueryParams } from "../../api/EventSlot/EventSlot";
import { useGetEventSlot, useQuoteEventSlots } from "../../api/EventSlot/EventSlotApi";
import { QuoteAvailableDeduction } from "../quoteCartPage/components/QuoteAvailableDeduction";
import OverrideTheme from "../../components/OverrideTheme";
import { QuoteDetails } from "./QuoteDetails";
import QuoteTopHelp from "./components/QuoteTopHelp";
import { QuoteComments } from "./QuoteComments";
import { useConfig } from "../../components/config/ConfigContext";

const QuotePage = ({ match }: Props) => {
  const query = useQuery();
  const paymentSuccess = query.get("payment") !== null;

  eventAnalytic.useEffect(paymentSuccess ? EEventType.StripeGateWaySuccess : EEventType.PageQuoteView);
  const dispatch = useDispatch();
  const classes = quotePageStyles();
  const [cookies, setCookie] = useCookies();
  const [open, setOpen] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const [addonForRemove, setAddonForRemove] = useState<QuoteLineItem | null>(null);
  const [subscriptionForRemove, setSubscriptionForRemove] = useState<ProductSubscription | null>(null);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { isFeatureEnabled } = useConfig();

  const { pageState, error, quote, activeDeduction, infoAddon } = useSelector((state: RootState) => state.quote);

  const quoteId = match.params.id;

  const [eventSlotQuote] = useState<EventSlotQueryParams>({
    quoteId: quoteId,
    page: 1,
    pageSize: 100
  });

  const { data: eventSlots, isLoading: isEventLoading } = useQuoteEventSlots(
    eventSlotQuote,
    isFeatureEnabled(FeatureSchedule)
  );

  const { data: selectedEventSlot, isLoading: isEventLoadingSlot } = useGetEventSlot(quote?.eventSlotId);

  useEffect(() => {
    dispatch(fetchProductSubscriptions());
    dispatch(fetchQuote(quoteId, paymentSuccess));
    dispatch(syncQuote(quoteId, true));
  }, [quoteId, paymentSuccess, dispatch]);

  eventAnalytic.useQuoteEffect(quote);

  if (!quote || !subscriptions || isEventLoading || isEventLoadingSlot) {
    return <Loader />;
  }

  const isAvailableSchedule = eventSlots!?.items?.length > 0 ?? false;

  if (pageState === PageState.Error || error) {
    return <div>Error</div>;
  }

  if (cookies[CookieQuoteId] !== quote.id) {
    setCookie(CookieQuoteId, quote.id, { path: "/" });
  }

  const availableSubscriptions = quoteAvailableSubscriptions(quote, subscriptions);

  const onAddAddon = (addon: QuoteLineItem) => {
    if (addon.selected) {
      onRemoveAddon(addon);
      return;
    }
    eventAnalytic.record(addon);
    dispatch(
      addAddonQuote(quoteId, {
        addonId: addon.productId,
        quantity: addon.quantity
      } as AddonForm)
    );
    toast.success(addon.name + " has been added");
    onInfoDialogClose();
  };

  const onRemoveAddon = (addon: QuoteLineItem) => {
    setAddonForRemove(addon);
    onRemoveDialogOpen();
  };

  const handleRemove = (addon: QuoteLineItem) => {
    dispatch(
      removeAddonQuote(quoteId, {
        addonId: addon.productId
      } as AddonForm)
    );
    eventAnalytic.record(EEventType.RemovedAddon);
    toast.success(addon.name + " has been removed");
  };

  const onChangeQuantity = (addon: QuoteLineItem, quantity: number) => {
    dispatch(
      changeQuantityAddonQuote(quoteId, {
        addonId: addon.productId,
        quantity: quantity
      } as AddonForm)
    );
  };

  const onInfoDialogOpen = (addon: QuoteLineItem) => {
    eventAnalytic.record(EEventType.AddonViewDetails);
    setOpen(true);
    dispatch(setInfoAddon(addon));
  };

  const onInfoDialogClose = () => {
    setOpen(false);
  };

  const onRemoveDialogOpen = () => {
    setOpenRemove(true);
  };

  const onRemoveDialogClose = () => {
    setOpenRemove(false);
  };

  const payNextPageLink =
    !hasAddons(quote.addons) && !hasSubscriptions(availableSubscriptions) && !isAvailableSchedule
      ? `/quote/cart/${quote.id}`
      : `/quote/addon/${quote.id}`;

  const onAddSubscription = (subscription: ProductSubscription) => {
    const selected = quote.subscriptions?.some((i) => i.productId === subscription.id);
    if (selected) {
      onRemoveSubscription(subscription);
      return;
    }
    eventAnalytic.record(subscription);
    dispatch(addSubscriptionQuote(quoteId, mapProductSubscriptionToForm(subscription)));
    toast.success(subscription.name + " has been added");
  };

  const onRemoveSubscription = (subscription: ProductSubscription) => {
    setSubscriptionForRemove(subscription);
    onRemoveDialogOpen();
  };

  const handleRemoveSubscription = (subscription: ProductSubscription) => {
    dispatch(
      removeSubscriptionQuote(quoteId, {
        productId: subscription.id
      } as QuoteSubscriptionForm)
    );
    eventAnalytic.record(EEventType.RemovedSubscription);
    toast.success(subscription.name + " has been removed");
  };

  const WhiteTextTypography = withStyles({
    root: {
      color: "#FFFFFF"
    }
  })(Typography);

  return (
    <OverrideTheme>
      <div className={classes.root}>
        <QuoteRemoveDialog
          open={openRemove}
          onClose={onRemoveDialogClose}
          onRemove={handleRemove}
          addon={addonForRemove}
          subscription={subscriptionForRemove}
          onRemoveSubscription={handleRemoveSubscription}
        />
        <QuoteAddonInfoDialog
          open={open}
          addon={infoAddon}
          onAdd={onAddAddon}
          onClose={onInfoDialogClose}
          onChangeQuantity={onChangeQuantity}
          allowEdits={allowCustomerEdits(quote)}
        />
        {activeDeduction && (
          <DeductionInfoModal
            title={activeDeduction.name}
            description={activeDeduction.description}
            handleClose={() => {
              dispatch(closeDeductionInfoModal());
            }}
          />
        )}
        {paymentSuccess && (
          <Box my={2}>
            <Container maxWidth="lg">
              <Box bgcolor={colors.primary} borderRadius={5} p={2}>
                <WhiteTextTypography variant="h6" align="center">
                  You're booked! Our customer success team will contact you shortly to discuss next steps regarding your
                  installation.
                </WhiteTextTypography>
              </Box>
            </Container>
          </Box>
        )}
        <Box>
          <QuoteAvailableDeduction quote={quote} />
          <Container maxWidth="lg">
            <Box mt={5}>
              <QuoteTop
                quote={quote}
                payNextPageLink={payNextPageLink}
                isAvailableSchedule={isAvailableSchedule}
                selectedEventSlot={selectedEventSlot}
              />
            </Box>
          </Container>
        </Box>

        <Box mt={3}>
          <Container maxWidth="lg">
            <QuoteDetails
              quote={quote}
              onRemoveAddon={onRemoveAddon}
              onInfoDialogOpen={onInfoDialogOpen}
              payNextPageLink={payNextPageLink}
              isAvailableSchedule={isAvailableSchedule}
            />
          </Container>
        </Box>

        <Box mt={3} bgcolor={colors.alternate} p={2}>
          <Container maxWidth="lg">
            <QuoteComments comments={quote.customerText} />
            <Box mt={3}>
              <QuoteTopHelp />
            </Box>
          </Container>
        </Box>

        <Box mt={3}>
          <Container maxWidth="lg">
            <QuoteRecommends
              quote={quote}
              onAddAddon={onAddAddon}
              onInfoDialogOpen={onInfoDialogOpen}
              subscriptions={availableSubscriptions}
              onAddSubscription={onAddSubscription}
              onChangeQuantity={onChangeQuantity}
            />
          </Container>
        </Box>

        <Box mt={3}>
          <QuoteGuarantee type={quote?.type} />
        </Box>

        <Box className={classes.alternate} mt={3} py={3}>
          <ReviewContainer type={quote.type} />
        </Box>
      </div>
    </OverrideTheme>
  );
};

export default QuotePage;

const quotePageStyles = makeStyles(() => ({
  root: {
    "& .panel": {
      "background": colors.white,
      "&.alternate": {
        background: colors.alternate
      }
    },
    "& .slick-dots": {
      "bottom": "5px",
      "&  li": {
        "margin": 0,
        "&.slick-active button:before": {
          color: colors.secondary
        }
      }
    }
  },
  alternate: {
    background: colors.alternate
  },
  highlight: {
    "display": "flex",
    "justifyContent": "stretch",
    "marginTop": "1em",
    "backgroundColor": "#fff",
    "& p": {
      padding: "0.5em"
    },
    "& h1": {
      padding: "0 0.5em",
      display: "inline"
    }
  },
  halfimage: {
    objectFit: "cover",
    width: "100%",
    height: "300px"
  }
}));

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

type Props = RouteComponentProps<{ id: string }>;
